import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import NavBar from "@/components/NavBar";
import { Inbox, Layers, ClipboardList, ShieldCheck } from "lucide-react";

const TABS = [
  { key: "inbox", label: "Catalog Inbox", icon: <Inbox size={14} /> },
  { key: "collections", label: "Member Collections", icon: <Layers size={14} /> },
  { key: "requests", label: "Catalog Requests", icon: <ClipboardList size={14} /> },
  { key: "approvals", label: "Vendor Approvals", icon: <ShieldCheck size={14} /> },
];

export default function AdminCatalogsPage() {
  const [tab, setTab] = useState("inbox");
  return (
    <>
      <NavBar />
      <main className="max-w-7xl mx-auto px-5 sm:px-10 py-10">
        <section data-testid="admin-catalogs-page">
          <div className="overline">Vendor Catalogs</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-2">Catalogs & Collections</h1>
          <p className="mt-2 text-sm text-[#4A4A4A] max-w-2xl">
            Review vendor catalogs, curate member-facing collections, and act on member requests.
          </p>

          <div className="mt-6 flex flex-wrap gap-2 border-b border-[#E5E3D8]" data-testid="admin-catalog-tabs">
            {TABS.map((t) => (
              <button
                key={t.key} onClick={() => setTab(t.key)}
                data-testid={`admin-catalog-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.24em] font-semibold border-b-2 -mb-px transition-colors ${
                  tab === t.key ? "border-[#1A422D] text-[#1A422D]" : "border-transparent text-[#7A7A7A] hover:text-[#1A422D]"
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "inbox" && <CatalogInboxPanel />}
            {tab === "collections" && <MemberCollectionsPanel />}
            {tab === "requests" && <RequestsInboxPanel />}
            {tab === "approvals" && <VendorApprovalsPanel />}
          </div>
        </section>
      </main>
    </>
  );
}

// ------------------ Catalog Inbox ------------------

function CatalogInboxPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/catalog-inbox")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-[#7A7A7A]">Loading inbox…</div>;
  if (items.length === 0) return (
    <div className="p-8 border border-dashed border-[#B59A5A]/40 text-center" data-testid="admin-inbox-empty">
      <Inbox className="mx-auto text-[#B59A5A]" size={24} />
      <div className="mt-3 font-serif text-xl text-[#1A422D]">No catalogs waiting</div>
      <p className="text-sm text-[#4A4A4A] mt-2">When an approved vendor shares a catalog it will appear here.</p>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-inbox-list">
      {items.map((it) => (
        <Link
          key={it.share.share_id}
          to={`/admin/catalogs/${it.catalog.catalog_id}`}
          data-testid={`admin-inbox-card-${it.catalog.catalog_id}`}
          className="p-5 border border-[#E5E3D8] bg-white hover:border-[#B59A5A] transition-colors"
        >
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">{it.share.status}</div>
          <div className="mt-1 font-serif text-lg text-[#1A422D]">{it.catalog.name}</div>
          <div className="mt-1 text-xs text-[#7A7A7A]">
            {it.vendor?.company_name} · {it.product_count} products
          </div>
          <div className="mt-2 text-xs text-[#4A4A4A]">
            Shared {it.share.shared_at?.slice(0, 10)}
          </div>
        </Link>
      ))}
    </div>
  );
}

// ------------------ Member Collections ------------------

function MemberCollectionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/member-collections")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async (id) => {
    try { await api.post(`/admin/member-collections/${id}/publish`); load(); }
    catch (err) { alert(formatApiErrorDetail(err) || "Publish failed."); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this collection?")) return;
    try { await api.post(`/admin/member-collections/${id}/archive`); load(); }
    catch (err) { alert(formatApiErrorDetail(err) || "Archive failed."); }
  };

  if (loading) return <div className="text-sm text-[#7A7A7A]">Loading collections…</div>;
  if (items.length === 0) return (
    <div className="p-8 border border-dashed border-[#B59A5A]/40 text-center" data-testid="admin-collections-empty">
      <Layers className="mx-auto text-[#B59A5A]" size={24} />
      <div className="mt-3 font-serif text-xl text-[#1A422D]">No collections yet</div>
      <p className="text-sm text-[#4A4A4A] mt-2">Open a catalog from your inbox and select products to build a member collection.</p>
    </div>
  );

  return (
    <div className="border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]" data-testid="admin-collections-list">
      {items.map((c) => (
        <div key={c.collection_id} className="p-5 flex items-center justify-between gap-4" data-testid={`admin-collection-row-${c.collection_id}`}>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">{c.status}</div>
            <div className="mt-1 text-[#1A422D] font-medium truncate">{c.name}</div>
            <div className="text-xs text-[#7A7A7A] mt-1">
              {c.product_refs?.length || 0} products · Audience: {c.audience_type}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {c.status === "draft" && (
              <button onClick={() => publish(c.collection_id)} data-testid={`admin-collection-publish-${c.collection_id}`} className="btn-primary text-xs">Publish</button>
            )}
            {c.status !== "archived" && (
              <button onClick={() => archive(c.collection_id)} data-testid={`admin-collection-archive-${c.collection_id}`} className="btn-secondary text-xs">Archive</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------ Requests Inbox ------------------

function RequestsInboxPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/catalog-requests")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/catalog-requests/${id}/approve`);
      setFeedback({ type: "success", text: `Approved. Order ${data.order_id} created.` });
      load();
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err) || "Approve failed." });
    }
  };
  const decline = async (id) => {
    const reason = window.prompt("Decline reason (optional):", "") || "";
    try {
      await api.post(`/admin/catalog-requests/${id}/decline`, { reason });
      setFeedback({ type: "success", text: "Request declined." });
      load();
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err) || "Decline failed." });
    }
  };
  const availability = async (id) => {
    try {
      await api.post(`/admin/catalog-requests/${id}/request-availability`, { catalog_request_id: id });
      setFeedback({ type: "success", text: "Availability requested from vendor." });
      load();
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err) || "Request failed." });
    }
  };

  if (loading) return <div className="text-sm text-[#7A7A7A]">Loading requests…</div>;

  return (
    <div>
      {feedback && (
        <div className={`mb-4 text-xs ${feedback.type === "error" ? "text-[#a33]" : "text-[#1A422D]"}`} data-testid="admin-requests-feedback">
          {feedback.text}
        </div>
      )}
      {items.length === 0 ? (
        <div className="p-8 border border-dashed border-[#B59A5A]/40 text-center" data-testid="admin-requests-empty">
          <ClipboardList className="mx-auto text-[#B59A5A]" size={24} />
          <div className="mt-3 font-serif text-xl text-[#1A422D]">No pending requests</div>
        </div>
      ) : (
        <div className="border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]" data-testid="admin-requests-list">
          {items.map((r) => (
            <div key={r.request_id} className="p-5 flex flex-col md:flex-row md:items-start justify-between gap-4" data-testid={`admin-request-row-${r.request_id}`}>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">{r.status}</div>
                <div className="mt-1 text-[#1A422D] font-medium">{r.product?.product_name || r.product_id}</div>
                <div className="text-xs text-[#7A7A7A] mt-1">
                  {r.member?.name || "Member"} · {r.size || "?"} / {r.color || "?"} · Qty {r.quantity}
                </div>
                {r.special_instructions && (
                  <div className="text-xs text-[#4A4A4A] mt-2 italic">&ldquo;{r.special_instructions}&rdquo;</div>
                )}
              </div>
              {r.status === "submitted" || r.status === "under_review" || r.status === "availability_check" || r.status === "availability_confirmed" ? (
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => availability(r.request_id)} data-testid={`admin-request-availability-${r.request_id}`} className="btn-secondary text-xs">Request Availability</button>
                  <button onClick={() => approve(r.request_id)} data-testid={`admin-request-approve-${r.request_id}`} className="btn-primary text-xs">Approve → Order</button>
                  <button onClick={() => decline(r.request_id)} data-testid={`admin-request-decline-${r.request_id}`} className="text-xs text-[#a33] hover:underline">Decline</button>
                </div>
              ) : (
                <div className="text-xs text-[#7A7A7A]" data-testid={`admin-request-terminal-${r.request_id}`}>
                  {r.order_id ? `Order ${r.order_id}` : "Closed"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------ Vendor Approvals ------------------

function VendorApprovalsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorId, setVendorId] = useState("");
  const [notes, setNotes] = useState("");
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/vendor-approvals")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    if (!vendorId.trim()) return;
    try {
      await api.post("/admin/vendor-approvals", { vendor_id: vendorId.trim(), notes: notes.trim() || undefined });
      setFeedback({ type: "success", text: "Vendor approved." });
      setVendorId(""); setNotes("");
      load();
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err) || "Approval failed." });
    }
  };
  const revoke = async (approval_id) => {
    if (!window.confirm("Revoke this vendor? They can no longer share catalogs with your club.")) return;
    try { await api.post(`/admin/vendor-approvals/${approval_id}/revoke`); load(); }
    catch (err) { alert(formatApiErrorDetail(err) || "Revoke failed."); }
  };

  return (
    <div>
      <div className="p-5 border border-[#E5E3D8] bg-white mb-6" data-testid="approve-vendor-form">
        <h3 className="font-serif text-lg text-[#1A422D]">Approve a Vendor</h3>
        <p className="text-xs text-[#7A7A7A] mt-1">Paste the vendor&apos;s ID (from Super Admin → Vendors) to authorize them to share catalogs with your club.</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={vendorId} onChange={(e) => setVendorId(e.target.value)} placeholder="vendor_xxx" data-testid="approve-vendor-id" className="input-field md:col-span-1" />
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal note (optional)" data-testid="approve-vendor-notes" className="input-field md:col-span-1" />
          <button onClick={approve} data-testid="approve-vendor-submit" className="btn-primary md:col-span-1">Approve Vendor</button>
        </div>
        {feedback && <div className={`mt-3 text-xs ${feedback.type === "error" ? "text-[#a33]" : "text-[#1A422D]"}`}>{feedback.text}</div>}
      </div>

      {loading ? (
        <div className="text-sm text-[#7A7A7A]">Loading approvals…</div>
      ) : items.length === 0 ? (
        <div className="p-8 border border-dashed border-[#B59A5A]/40 text-center text-sm text-[#7A7A7A]" data-testid="approvals-empty">
          No vendor approvals yet.
        </div>
      ) : (
        <div className="border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]" data-testid="approvals-list">
          {items.map((a) => (
            <div key={a.approval_id} className="p-4 flex items-center justify-between" data-testid={`approval-row-${a.approval_id}`}>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">{a.status}</div>
                <div className="text-[#1A422D] font-medium">{a.vendor?.company_name || a.vendor_id}</div>
                <div className="text-xs text-[#7A7A7A]">{a.vendor?.email}</div>
              </div>
              {a.status === "approved" && (
                <button onClick={() => revoke(a.approval_id)} data-testid={`approval-revoke-${a.approval_id}`} className="text-xs text-[#a33] hover:underline">Revoke</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
